import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { Role } from 'context/authentication/Role.types'
import { TripDetailsContext } from 'context/trips/TripDetailsContext'
import { hasAccess } from 'global/helpers/authorization'
import type { ErrorMessage } from 'global/helpers/requestError'
import useDialog from 'global/hooks/useDialog'
import { type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import BulkDocumentsUploadDialog from 'screens/StatusManager/BulkDocumentsUploadDialog'
import BulkStatusUpdateDialog from 'screens/StatusManager/BulkStatusUpdateDialog'
import type { TripData } from 'screens/TripDashboard/TripDashboard.types'
import PrimaryActions from 'screens/TripDetails/PrimaryActions'
import StatusHistory from 'screens/TripDetails/StatusHistory'
import {
  isBulkStatusUpdateAvailable,
  isScdBulkDocumentsUploadAvailable,
  processStopLegPairsForBulkUpdates,
} from 'screens/TripDetails/StopsView/StopsView.helpers'
import { getGroupedStopStatuses } from 'screens/TripDetails/TripDetails.helpers'
import {
  type KNDataCardAction,
  type StopsSequenceGroup,
  type StopStatus,
} from 'screens/TripDetails/TripDetails.types'

interface StopGroupActionsProps {
  group: StopsSequenceGroup
  trip: TripData
  weblinkToken?: string
}

const StopGroupActions = ({
  group,
  trip,
  weblinkToken,
}: StopGroupActionsProps): ReactNode => {
  const { t } = useTranslation()
  const [groupStatuses, setGroupStatuses] = useState<StopStatus[]>([])
  const [_tripDetailsState, tripDetailsDispatch] = useContext(TripDetailsContext)

  const {
    withAvailableRegularStatuses: pickupStopLegPairsWithAvailableRegularStatuses,
    withAvailableDiscrepancyStatuses: pickupStopLegPairsWithAvailableDiscrepancyStatuses,
    forScdBulkDocumentsUpload: pickupStopLegPairsForScdBulkDocumentsUpload,
  } = useMemo(() => processStopLegPairsForBulkUpdates(group.stopLegPairs, 'PUP'), [group.stopLegPairs])

  const {
    withAvailableRegularStatuses: deliveryStopLegPairsWithAvailableRegularStatuses,
    withAvailableDiscrepancyStatuses: deliveryStopLegPairsWithAvailableDiscrepancyStatuses,
    forScdBulkDocumentsUpload: deliveryStopLegPairsForScdBulkDocumentsUpload,
  } = useMemo(() => processStopLegPairsForBulkUpdates(group.stopLegPairs, 'DEL'), [group.stopLegPairs])

  const {
    withAvailableRegularStatuses: customsStopLegPairsWithAvailableRegularStatuses,
    withAvailableDiscrepancyStatuses: customsStopLegPairsWithAvailableDiscrepancyStatuses,
  } = useMemo(() => processStopLegPairsForBulkUpdates(group.stopLegPairs, 'CUS'), [group.stopLegPairs])

  useEffect(() => {
    setGroupStatuses(getGroupedStopStatuses(group.stopLegPairs))
  }, [group.stopLegPairs])

  const handleDialogOpen = useCallback(() => {
    tripDetailsDispatch({
      type: 'clearStopsErrors',
    })
  }, [])

  const handleDialogError = useCallback((errors: ErrorMessage[]) => {
    bulkPickupRegularStatusUpdateDialog.handleClose()
    bulkPickupDiscrepancyStatusUpdateDialog.handleClose()
    bulkPickupDocumentsUploadDialog.handleClose()
    bulkDeliveryRegularStatusUpdateDialog.handleClose()
    bulkDeliveryDiscrepancyStatusUpdateDialog.handleClose()
    bulkDeliveryDocumentsUploadDialog.handleClose()
    bulkCustomsRegularStatusUpdateDialog.handleClose()
    bulkCustomsDiscrepancyStatusUpdateDialog.handleClose()
    tripDetailsDispatch({
      type: 'setStopsErrors',
      payload: errors,
    })
  }, [])

  const bulkPickupRegularStatusUpdateDialog = useDialog<typeof BulkStatusUpdateDialog>({
    onOpen: handleDialogOpen,
    onError: handleDialogError,
  })

  const bulkPickupDiscrepancyStatusUpdateDialog = useDialog<typeof BulkStatusUpdateDialog>({
    onOpen: handleDialogOpen,
    onError: handleDialogError,
  })

  const bulkPickupDocumentsUploadDialog = useDialog<typeof BulkDocumentsUploadDialog>({
    onOpen: handleDialogOpen,
    onError: handleDialogError,
  })

  const bulkDeliveryRegularStatusUpdateDialog = useDialog<typeof BulkStatusUpdateDialog>({
    onOpen: handleDialogOpen,
    onError: handleDialogError,
  })

  const bulkDeliveryDiscrepancyStatusUpdateDialog = useDialog<typeof BulkStatusUpdateDialog>({
    onOpen: handleDialogOpen,
    onError: handleDialogError,
  })

  const bulkDeliveryDocumentsUploadDialog = useDialog<typeof BulkDocumentsUploadDialog>({
    onOpen: handleDialogOpen,
    onError: handleDialogError,
  })

  const bulkCustomsRegularStatusUpdateDialog = useDialog<typeof BulkStatusUpdateDialog>({
    onOpen: handleDialogOpen,
    onError: handleDialogError,
  })

  const bulkCustomsDiscrepancyStatusUpdateDialog = useDialog<typeof BulkStatusUpdateDialog>({
    onOpen: handleDialogOpen,
    onError: handleDialogError,
  })

  const actions: KNDataCardAction[] = []
  if (isBulkStatusUpdateAvailable(pickupStopLegPairsWithAvailableRegularStatuses)) {
    actions.push({
      label: t('screens.cs.trip_details.card.actions.bulk_pickup_update_status'),
      handler: bulkPickupRegularStatusUpdateDialog.handleOpen,
    })
  }
  if (isBulkStatusUpdateAvailable(pickupStopLegPairsWithAvailableDiscrepancyStatuses)) {
    actions.push({
      icon: <WarningAmberOutlinedIcon />,
      color: 'error',
      variant: 'outlined',
      handler: bulkPickupDiscrepancyStatusUpdateDialog.handleOpen,
    })
  }
  // bulk document update is SCD-only, shows up only when all pickup stops are ready for document upload
  if (isScdBulkDocumentsUploadAvailable(pickupStopLegPairsWithAvailableRegularStatuses, pickupStopLegPairsForScdBulkDocumentsUpload)) {
    actions.push({
      label: t('screens.cs.trip_details.card.actions.bulk_pickup_update_documents'),
      handler: bulkPickupDocumentsUploadDialog.handleOpen,
    })
  }
  if (isBulkStatusUpdateAvailable(deliveryStopLegPairsWithAvailableRegularStatuses)) {
    actions.push({
      label: t('screens.cs.trip_details.card.actions.bulk_delivery_update_status'),
      handler: bulkDeliveryRegularStatusUpdateDialog.handleOpen,
    })
  }
  if (isBulkStatusUpdateAvailable(deliveryStopLegPairsWithAvailableDiscrepancyStatuses)) {
    actions.push({
      icon: <WarningAmberOutlinedIcon />,
      color: 'error',
      variant: 'outlined',
      handler: bulkDeliveryDiscrepancyStatusUpdateDialog.handleOpen,
    })
  }
  // bulk document update is SCD-only, shows up only when all delivery stops are ready for document upload
  if (isScdBulkDocumentsUploadAvailable(deliveryStopLegPairsWithAvailableRegularStatuses, deliveryStopLegPairsForScdBulkDocumentsUpload)) {
    actions.push({
      label: t('screens.cs.trip_details.card.actions.bulk_delivery_update_documents'),
      handler: bulkDeliveryDocumentsUploadDialog.handleOpen,
    })
  }
  if (isBulkStatusUpdateAvailable(customsStopLegPairsWithAvailableRegularStatuses)) {
    actions.push({
      label: t('screens.cs.trip_details.card.actions.bulk_customs_update_status'),
      handler: bulkCustomsRegularStatusUpdateDialog.handleOpen,
    })
  }
  if (isBulkStatusUpdateAvailable(customsStopLegPairsWithAvailableDiscrepancyStatuses)) {
    actions.push({
      icon: <WarningAmberOutlinedIcon />,
      color: 'error',
      variant: 'outlined',
      handler: bulkCustomsDiscrepancyStatusUpdateDialog.handleOpen,
    })
  }

  if (groupStatuses.length === 0 && actions.length === 0) {
    return null
  }

  return (
    <>
      <Stack spacing={2}>
        {groupStatuses.length > 0 && (
          <Stack>
            <Typography variant="p6" color="primary.light" component="p">
              {t('screens.cs.trip_details.update_status.status')}
            </Typography>
            <StatusHistory statuses={groupStatuses} />
          </Stack>
        )}
        {(hasAccess(Role.Editor) || weblinkToken) && (
          <PrimaryActions actions={actions} />
        )}
      </Stack>

      {isBulkStatusUpdateAvailable(pickupStopLegPairsWithAvailableRegularStatuses) && (
        <BulkStatusUpdateDialog
          payload={{
            trip,
            stopLegPairs: pickupStopLegPairsWithAvailableRegularStatuses,
            weblinkToken,
            noDiscrepancyStatuses: true,
          }}
          open={bulkPickupRegularStatusUpdateDialog.isOpen}
          onAction={bulkPickupRegularStatusUpdateDialog.handleAction}
          onError={bulkPickupRegularStatusUpdateDialog.handleError}
          onClose={bulkPickupRegularStatusUpdateDialog.handleClose}
        />
      )}
      {isBulkStatusUpdateAvailable(pickupStopLegPairsWithAvailableDiscrepancyStatuses) && (
        <BulkStatusUpdateDialog
          payload={{
            trip,
            stopLegPairs: pickupStopLegPairsWithAvailableDiscrepancyStatuses,
            weblinkToken,
            noRegularStatuses: true,
          }}
          open={bulkPickupDiscrepancyStatusUpdateDialog.isOpen}
          onAction={bulkPickupDiscrepancyStatusUpdateDialog.handleAction}
          onError={bulkPickupDiscrepancyStatusUpdateDialog.handleError}
          onClose={bulkPickupDiscrepancyStatusUpdateDialog.handleClose}
        />
      )}
      {isScdBulkDocumentsUploadAvailable(pickupStopLegPairsWithAvailableRegularStatuses, pickupStopLegPairsForScdBulkDocumentsUpload) && (
        <BulkDocumentsUploadDialog
          payload={{
            trip,
            stopLegPairs: pickupStopLegPairsForScdBulkDocumentsUpload,
            weblinkToken,
          }}
          open={bulkPickupDocumentsUploadDialog.isOpen}
          onAction={bulkPickupDocumentsUploadDialog.handleAction}
          onError={bulkPickupDocumentsUploadDialog.handleError}
          onClose={bulkPickupDocumentsUploadDialog.handleClose}
        />
      )}

      {isBulkStatusUpdateAvailable(deliveryStopLegPairsWithAvailableRegularStatuses) && (
        <BulkStatusUpdateDialog
          payload={{
            trip,
            stopLegPairs: deliveryStopLegPairsWithAvailableRegularStatuses,
            weblinkToken,
            noDiscrepancyStatuses: true,
          }}
          open={bulkDeliveryRegularStatusUpdateDialog.isOpen}
          onAction={bulkDeliveryRegularStatusUpdateDialog.handleAction}
          onError={bulkDeliveryRegularStatusUpdateDialog.handleError}
          onClose={bulkDeliveryRegularStatusUpdateDialog.handleClose}
        />
      )}
      {isBulkStatusUpdateAvailable(deliveryStopLegPairsWithAvailableDiscrepancyStatuses) && (
        <BulkStatusUpdateDialog
          payload={{
            trip,
            stopLegPairs: deliveryStopLegPairsWithAvailableDiscrepancyStatuses,
            weblinkToken,
            noRegularStatuses: true,
          }}
          open={bulkDeliveryDiscrepancyStatusUpdateDialog.isOpen}
          onAction={bulkDeliveryDiscrepancyStatusUpdateDialog.handleAction}
          onError={bulkDeliveryDiscrepancyStatusUpdateDialog.handleError}
          onClose={bulkDeliveryDiscrepancyStatusUpdateDialog.handleClose}
        />
      )}
      {isScdBulkDocumentsUploadAvailable(deliveryStopLegPairsWithAvailableRegularStatuses, deliveryStopLegPairsForScdBulkDocumentsUpload) && (
        <BulkDocumentsUploadDialog
          payload={{
            trip,
            stopLegPairs: deliveryStopLegPairsForScdBulkDocumentsUpload,
            weblinkToken,
          }}
          open={bulkDeliveryDocumentsUploadDialog.isOpen}
          onAction={bulkDeliveryDocumentsUploadDialog.handleAction}
          onError={bulkDeliveryDocumentsUploadDialog.handleError}
          onClose={bulkDeliveryDocumentsUploadDialog.handleClose}
        />
      )}

      {isBulkStatusUpdateAvailable(customsStopLegPairsWithAvailableRegularStatuses) && (
        <BulkStatusUpdateDialog
          payload={{
            trip,
            stopLegPairs: customsStopLegPairsWithAvailableRegularStatuses,
            weblinkToken,
            noDiscrepancyStatuses: true,
          }}
          open={bulkCustomsRegularStatusUpdateDialog.isOpen}
          onAction={bulkCustomsRegularStatusUpdateDialog.handleAction}
          onError={bulkCustomsRegularStatusUpdateDialog.handleError}
          onClose={bulkCustomsRegularStatusUpdateDialog.handleClose}
        />
      )}
      {isBulkStatusUpdateAvailable(customsStopLegPairsWithAvailableDiscrepancyStatuses) && (
        <BulkStatusUpdateDialog
          payload={{
            trip,
            stopLegPairs: customsStopLegPairsWithAvailableDiscrepancyStatuses,
            weblinkToken,
            noRegularStatuses: true,
          }}
          open={bulkCustomsDiscrepancyStatusUpdateDialog.isOpen}
          onAction={bulkCustomsDiscrepancyStatusUpdateDialog.handleAction}
          onError={bulkCustomsDiscrepancyStatusUpdateDialog.handleError}
          onClose={bulkCustomsDiscrepancyStatusUpdateDialog.handleClose}
        />
      )}
    </>
  )
}

export default StopGroupActions
